/**
 * iter-338v (BOQEditPage Refactor Phase A):
 * Merge Estimate modal — appends line items + tasks from another estimate
 * into the current BOQ worksheet. Uses a plain overlay (not the shadcn
 * Dialog) matching the original behaviour.
 */
import { Layers, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function MergeItemsDialog({
  open,
  onOpenChange,
  merging,
  estimateId,
  onEstimateIdChange,
  createTasks,
  onCreateTasksChange,
  availableEstimates,
  onConfirm,
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={() => !merging && onOpenChange(false)}
      data-testid="merge-estimate-modal"
    >
      <div className="bg-white rounded-xl max-w-xl w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2">
              <Layers size={18} className="text-emerald-600" />
              Add from Estimate
            </h3>
            <p className="text-xs text-stone-500 mt-0.5">
              Append line items + tasks from another estimate into this worksheet. Sections auto-group by MK / MW / G.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-stone-400 hover:text-stone-700"
            disabled={merging}
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-stone-700 mb-1 block">Select Estimate</label>
            <select
              className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={estimateId}
              onChange={(e) => onEstimateIdChange(e.target.value)}
              data-testid="merge-estimate-select"
            >
              <option value="">— Pick an estimate —</option>
              {availableEstimates.length === 0 && (
                <option value="" disabled>No available estimates for this project</option>
              )}
              {availableEstimates.map((e) => {
                const isKit = (e.config?.productType === 'kitchen') || !!e.config?.kitchen;
                return (
                  <option key={e.id} value={e.id}>
                    {`${isKit ? '🍳 Kitchen' : '🛏 Wardrobe'} · ${e.name} · Rs ${Math.round(e.total_cost || 0).toLocaleString('en-IN')}`}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="merge-create-tasks"
              checked={createTasks}
              onChange={(e) => onCreateTasksChange(e.target.checked)}
              data-testid="merge-create-tasks-toggle"
            />
            <label htmlFor="merge-create-tasks" className="text-sm text-stone-700">
              Also create Tasks in Tasks &amp; Schedule for each merged line
            </label>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
            <p className="font-semibold mb-1 flex items-center gap-1"><ArrowRight size={12} />What happens when you merge?</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>All estimate sections (Module Breakdown / Countertop / Backsplash / Sink &amp; Plumbing / Electrical / Handles / Labour) are appended as BOQ lines.</li>
              <li>Main category auto-maps (Kitchen → MK, Wardrobe → MW / G).</li>
              <li>Each line optionally becomes a Task under the correct category.</li>
              <li>Source estimate remains fully editable.</li>
            </ul>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t bg-stone-50 rounded-b-xl">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={merging}>Cancel</Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={merging || !estimateId}
            data-testid="merge-estimate-confirm-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Layers size={14} className="mr-1" />{merging ? 'Merging...' : 'Merge into Worksheet'}
          </Button>
        </div>
      </div>
    </div>
  );
}
